import { APIContext } from "../../types";
import { makePath } from "../../../../api/url";
import { Side } from "../types";
import { applyParams } from "../../../params";
import { getJSON, getRaw, postJSON } from "../../request";
import { Point2D, Line, SMPLBodyMetrics } from "./types";

export type MeshugaAPI = ReturnType<typeof createMeshugaAPI>;

export function createMeshugaAPI({ buildURL, fetchJSON, fetchAs }: APIContext) {
  return {
    fetch3D({
      deviceId,
      recordingId,
      pose,
    }: {
      deviceId: string;
      recordingId: string;
      pose: Side;
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "glb",
      ]);

      const url = buildURL(path);
      url.searchParams.set("pose", pose);

      const request = getRaw(url);
      return fetchAs(request, (res) => res.arrayBuffer());
    },

    fetchPointCloud({
      deviceId,
      recordingId,
      pose,
      camera,
      nofilter,
      nobb,
      depthcull,
      downsample,
      nocolor,
    }: {
      deviceId: string;
      recordingId: string;
      pose: Side;
      camera?: string;
      nofilter?: boolean;
      nobb?: boolean;
      depthcull?: number;
      downsample?: string;
      nocolor?: boolean;
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "pcd",
      ]);

      const url = buildURL(path);

      applyParams(url.searchParams, {
        pose,
        module: camera ? camera : undefined,
        nofilter: nofilter ? "1" : undefined,
        nobb: nobb ? "1" : undefined,
        depthcull,
        downsample,
        nocolor: nocolor ? "1" : undefined,
      });

      const request = getRaw(url);

      return fetchAs(request, (res) => res.arrayBuffer());
    },

    fetchSMPLBodyMetrics({
      deviceId,
      recordingId,
      pose,
      kind,
      source,
    }: {
      deviceId: string;
      recordingId: string;
      pose: "identity" | "front" | "back";
      kind: "neutral" | "female" | "male";
      source?: "inference" | "refined";
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "measurements",
      ]);

      const url = buildURL(path);

      applyParams(url.searchParams, {
        pose,
        kind,
        source,
      });

      const request = getJSON(url);
      return fetchJSON<SMPLBodyMetrics>(request);
    },

    /**
     * @deprecated Use fetchSMPLXPointCloud instead
     */
    fetchSMPLPointCloud({
      deviceId,
      recordingId,
      pose,
      kind,
      source,
    }: {
      deviceId: string;
      recordingId: string;
      pose: "identity" | "front" | "back";
      kind: "neutral" | "female" | "male";
      source?: "inference" | "refined";
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "smplpcd",
      ]);

      const url = buildURL(path);

      applyParams(url.searchParams, {
        pose,
        kind,
        source,
      });

      const request = getRaw(url);

      return fetchAs(request, (res) => res.arrayBuffer());
    },

    fetchMeasurement({
      deviceId,
      recordingId,
      camera,
      pose,
      line,
    }: {
      deviceId: string;
      recordingId: string;
      camera: string;
      pose: Side;
      line: Line;
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "camera",
        camera,
        "distance",
      ]);

      const url = buildURL(path);

      applyParams(url.searchParams, {
        pose,
        x1: line.a.x,
        y1: line.a.y,
        x2: line.b.x,
        y2: line.b.y,
      });

      const request = getJSON(url);

      return fetchJSON<{ distance: number | null }>(request);
    },

    fetchPoints({
      deviceId,
      recordingId,
      camera,
      pose,
      points,
    }: {
      deviceId: string;
      recordingId: string;
      camera: string;
      pose: Side;
      points: Point2D[];
    }) {
      const path = makePath([
        "meshuga",
        "v1",
        "device",
        deviceId,
        "recording",
        recordingId,
        "pointlookup",
      ]);

      const url = buildURL(path);

      const payload = points.map((p) => {
        return {
          pose: pose,
          camera: camera,
          xy: [p.x, p.y],
        };
      });

      const request = postJSON(url, payload);
      return fetchJSON<{ xyz: [number, number, number] }[]>(request);
    },
  };
}
